"use client";

import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useCheckoutBase, defaultGuest } from "./use-checkout-base";
import {
  resolveGuestCollectionMode,
  hasRenderableQuestions,
} from "../utils/guest-questions";
import { resolveSessionLocationName } from "../utils/session-location";
import { effectivePreselectAvailability } from "./preselect-availability";
import { participantsToPayload } from "../participants-step";
import { buildTicketSeatGroups, totalSeatCount } from "../utils/ticket-seat-groups";
import { getLegacyManualPrice } from "../../../lib/pricing";
import type {
  Session,
  TicketType,
  TicketSelection,
  CheckoutStep,
  Guest,
} from "../utils/types";

export type UseCheckoutTrialProps = {
  course: any;
  session?: any;
  sessions?: any[];
  ticketType: any; // Single ticket type for trial
  locations?: any[]; // Locations for resolving location names
  user?: any;
  bundleID?: string;
  bundle?: any;
  isSendingMaterial?: boolean;
  isTrial?: boolean; // Whether this is a trial booking (true) or drop-in (false)
  /** Pre-select a session by ID (skips date selection step when session is found) */
  initialSessionId?: string;
  /** Seats reserved for this buyer per session (membership allocation) — added
   * to general availability when deciding sold-out, mirroring DateSelectionStep. */
  reservedSessionQuantities?: Record<string, number>;
  /**
   * Whether the listing allows the buyer to skip per-guest details.
   * Frozen and no longer consulted — buyer details are always collected
   * downstream in the basket checkout.
   */
  allowPrefillOthersGuestsOnCheckout?: boolean;
  /** Whether the school collects attendees via the participants step. */
  participantsEnabled?: boolean;
  /** Whether the school has any checkout-eligible participant fields. */
  hasParticipantFields?: boolean;
  onLoadMoreDates: (args: { page: number; limit: number; sessionId?: string }) => Promise<any>;
  onValidateDiscount?: (code: string, email: string) => Promise<any>;
  onStepChange?: () => void;
};

/**
 * Hook for trial/drop-in checkout
 * Flow: Dates -> Tickets -> UserForm -> Basket choice
 * No add-ons, single ticket type only
 * Use isTrial=true for trials, isTrial=false for drop-in bookings
 */
export function useCheckoutTrial({
  course,
  session,
  sessions: initialSessions,
  ticketType: initialTicketType,
  locations = [],
  user,
  bundleID,
  bundle,
  isSendingMaterial = false,
  isTrial = true,
  initialSessionId,
  reservedSessionQuantities,
  participantsEnabled = false,
  hasParticipantFields = false,
  onLoadMoreDates,
  onValidateDiscount,
  onStepChange,
}: UseCheckoutTrialProps) {
  const showParticipantsStep = participantsEnabled && hasParticipantFields;
  const page = useRef(initialSessions === undefined ? 0 : 1);
  // Store ticket type ref for access in callbacks
  const ticketTypeRef = useRef<any>(initialTicketType);
  // Store raw sessions for session-specific ticket type lookup
  const rawSessionsRef = useRef<any[]>(initialSessions ?? []);

  // Base checkout functionality - no add-ons for trial
  const base = useCheckoutBase({
    user,
    enabledAddOns: [], // No add-ons for trial
    isSendingMaterial,
    onStepChange,
  });

  // Location NAME for a session (shared resolver — utils/session-location.ts).
  // undefined → the session row renders no location pin.
  const getSessionLocation = useCallback(
    (s: any): string | undefined => resolveSessionLocationName(s, locations),
    [locations],
  );

  // Helper to get price - trial uses the ticket type price
  // @MM-177: When course.isPriceFrom is true, use the course's configured price
  const getSessionPrice = useCallback(
    (s: any): number | undefined => {
      // @MM-177: If course has priceFrom enabled, use the course's configured price
      // This allows schools to override the displayed price in the date selection step
      const legacyManualPrice = getLegacyManualPrice(course);
      if (legacyManualPrice !== undefined) {
        return legacyManualPrice;
      }

      // Live ticket price wins over the deprecated denormalised
      // session.price, which goes stale when the ticket is repriced.
      if (initialTicketType?.price !== undefined) {
        return initialTicketType.price;
      }

      if (s.price !== undefined) {
        return s.price;
      }

      // Final fallback: use course price if available
      return course?.price;
    },
    [
      initialTicketType?.price,
      course?.price,
      course?.isPriceFrom,
      course?.priceDisplayMode,
    ],
  );

  // Map sessions - use ticketType price for trial sessions
  const mapSession = useCallback(
    (s: any): Session => ({
      id: s._id,
      date: s.startDateTime,
      description: s.description,
      image: s.image,
      startDateTime: s.startDateTime,
      duration: s.duration,
      availability: s.availability,
      isOnline: s.isOnline ?? course?.isOnline,
      isTerm: false,
      // Resolve location using helper function
      location: getSessionLocation(s),
      // Get price using helper function
      price: getSessionPrice(s),
      tutorId: s.tutor, // Tutor ID for lookup
      tutorName: s.tutorName || s.tutor?.name,
      tutorImage: s.tutorImage || s.tutor?.image?.url,
    }),
    [getSessionLocation, getSessionPrice, course?.isOnline],
  );

  // Sessions state
  const [sessions, setSessions] = useState<Session[]>(() =>
    (initialSessions ?? []).map(mapSession),
  );
  const [selectedSession, setSelectedSession] = useState<Session | null>(() =>
    session ? mapSession(session) : null,
  );
  const [isFullyLoaded, setIsFullyLoaded] = useState(
    initialSessions !== undefined && initialSessions.length < 15,
  );

  // Track if we've applied the initial session selection
  const hasAppliedInitialSessionRef = useRef(false);

  // Single ticket type for trial
  const ticketTypes = useMemo((): TicketType[] => {
    if (!initialTicketType) return [];

    // Find raw session to get session-specific ticket type configuration
    const rawSession = selectedSession
      ? rawSessionsRef.current.find((rs) => rs._id === selectedSession.id)
      : null;

    // Get session-specific ticket type for maxLimit/currentSold
    const sessionTicketType = rawSession?.ticketTypes?.find(
      (stt: any) => stt.ticketTypeId === initialTicketType._id,
    );

    // For trial sessions (single session), no typeCharge adjustments needed
    const effectivePrice = initialTicketType.price;

    return [
      {
        id: initialTicketType._id,
        name: initialTicketType.name,
        description: initialTicketType.description,
        price: initialTicketType.price,
        effectivePrice, // Add computed effective price
        maxQuantity: initialTicketType.maxQuantity ?? Infinity,
        minQuantity: initialTicketType.minQuantity ?? 0,
        soldOut: initialTicketType.soldOut,
        // Session-specific availability fields - use session values if available
        numberOfGuestsPerTicket: initialTicketType.numberOfGuestsPerTicket ?? 1,
        // Soft age range (booking seats Phase 5): warns, never blocks.
        minAge: initialTicketType.minAge ?? null,
        maxAge: initialTicketType.maxAge ?? null,
        maxLimit: sessionTicketType?.maxLimit ?? initialTicketType.maxLimit,
        currentSold: sessionTicketType?.currentSold ?? initialTicketType.currentSold ?? 0,
        isInTimeConstraint: initialTicketType.isInTimeConstraint,
        typeCharge: initialTicketType.typeCharge,
      },
    ];
  }, [initialTicketType, selectedSession]);

  // Ticket selections - single ticket for trial
  const [ticketSelections, setTicketSelections] = useState<TicketSelection[]>(
    [],
  );

  // Pre-fill personal details from bundle if available
  useEffect(() => {
    if (bundle) {
      const nameParts = bundle.buyerName?.split(" ") || [];
      const bundleGuest: Guest = {
        ...defaultGuest,
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ") || "",
        email: bundle.buyerEmail || "",
        phoneNumber: bundle.buyerPhone || "",
      };
      base.setPersonalDetails((prev) => ({
        ...prev,
        guests: [bundleGuest],
      }));
    }
  }, [bundle]);

  // Current step - start at tickets if session is pre-selected, otherwise dates
  const [step, setStep] = useState<CheckoutStep>(() =>
    session ? "tickets" : "dates",
  );

  // Pre-select session by ID when initialSessionId is provided and sessions are loaded
  // The sessionId is passed to the backend on initial load to ensure it's included
  useEffect(() => {
    if (
      !initialSessionId ||
      hasAppliedInitialSessionRef.current ||
      sessions.length === 0
    ) {
      return;
    }

    // Find the session matching the initialSessionId
    const matchingSession = sessions.find((s) => s.id === initialSessionId);

    if (matchingSession) {
      hasAppliedInitialSessionRef.current = true;
      setSelectedSession(matchingSession);
      // A SOLD-OUT preselected session (e.g. a queue-held session/drop-in reached
      // via a timetable "Book Now" deep link) must land on the dates step's
      // sold-out gate, not auto-advance into a doomed checkout. Uses EFFECTIVE
      // availability (server availability minus the buyer's own basket-held seats,
      // matching the date card); a claimer's ?offer= token keeps their held seats
      // visible server-side, so they still auto-advance.
      if (
        effectivePreselectAvailability(matchingSession, reservedSessionQuantities) <= 0
      ) {
        setStep("dates");
        return;
      }
      // Move to tickets step since date is pre-selected
      setStep("tickets");
    }
    // Note: If session not found after initial load with sessionId param,
    // user will see the date selection (backend should ensure session is included)
  }, [initialSessionId, sessions, reservedSessionQuantities]);

  // Calculate total
  // For bundle bookings, total is 0 (payment was done when purchasing bundle)
  const total = useMemo(() => {
    if (bundleID) return 0;

    const ticketsTotal = ticketSelections.reduce((sum, selection) => {
      const ticketType = ticketTypes.find(
        (tt) => tt.id === selection.ticketTypeId,
      );
      return sum + (ticketType?.price ?? 0) * selection.quantity;
    }, 0);

    // Calculate discount based on type
    let discountAmount = 0;
    if (base.discount) {
      const { value, type } = base.discount;
      switch (type) {
        case "PERCENTAGE":
          discountAmount = Math.round(
            (ticketsTotal * Math.min(value, 100)) / 100,
          );
          break;
        case "PRICE":
        default:
          discountAmount = Math.min(ticketsTotal, value);
          break;
      }
    }

    return Math.max(0, ticketsTotal - discountAmount);
  }, [ticketSelections, ticketTypes, base.discount, bundleID]);

  // Calculate the discount amount for display purposes
  const discountAmount = useMemo(() => {
    if (bundleID) return 0;
    if (!base.discount) return 0;

    const ticketsTotal = ticketSelections.reduce((sum, selection) => {
      const ticketType = ticketTypes.find(
        (tt) => tt.id === selection.ticketTypeId,
      );
      return sum + (ticketType?.price ?? 0) * selection.quantity;
    }, 0);

    const { value, type } = base.discount;

    switch (type) {
      case "PERCENTAGE":
        return Math.round((ticketsTotal * Math.min(value, 100)) / 100);
      case "PRICE":
      default:
        return Math.min(ticketsTotal, value);
    }
  }, [ticketSelections, ticketTypes, base.discount, bundleID]);

  // Is free - if bundleID, payment already made
  const isFree = useMemo(() => {
    if (bundleID) return true;
    return ticketSelections.some((s) => s.quantity > 0) && total === 0;
  }, [ticketSelections, total, bundleID]);

  // Remaining availability - session availability minus selected tickets (considering guests per ticket)
  const remainingAvailability = useMemo(() => {
    if (!selectedSession) return 0;

    const baseAvailability = selectedSession.availability ?? 0;

    // Calculate total "seats" taken by current selections
    const selectedSeats = ticketSelections.reduce((sum, selection) => {
      const ticketType = ticketTypes.find(
        (tt) => tt.id === selection.ticketTypeId,
      );
      const guestsPerTicket = ticketType?.numberOfGuestsPerTicket ?? 1;
      return sum + selection.quantity * guestsPerTicket;
    }, 0);

    return Math.max(0, baseAvailability - selectedSeats);
  }, [selectedSession, ticketSelections, ticketTypes]);

  // Visible steps - no add-ons for trial
  // Total guest count (seats) derived from current ticket selections.
  // Seats grouped by ticket, the one derivation every seat count reads from
  // (mirrors the server's computeReserveSeatCounts / BookingSeat.planSeats).
  const ticketSeatGroups = useMemo(
    () => buildTicketSeatGroups(ticketSelections, ticketTypes),
    [ticketSelections, ticketTypes],
  );
  const totalGuestCount = useMemo(() => totalSeatCount(ticketSeatGroups), [ticketSeatGroups]);

  // How this listing collects guests: additional guests come via the booking
  // form when it carries the (enabled) system identity questions; without
  // them, guest details simply aren't collected. The buyer is always
  // collected at the basket page, so there is no in-flow personal step.
  // Drop-in reuses this hook and can be multi-guest; trial is single-guest.
  const { collectSystemGuests } = resolveGuestCollectionMode(
    course.userQuestionForm?.questions,
    {
      formEnabled: course.userQuestionForm?.isEnabled,
    },
  );

  const visibleSteps = useMemo(() => {
    const steps: CheckoutStep[] = ["dates", "tickets"];

    // No add-ons step for trial

    // Participants ("Who is attending?") — collected before the booking-question
    // form. When enabled it replaces the system guest-identity questions.
    if (showParticipantsStep) {
      steps.push("participants");
    }

    // Show the booking-question step when the form is enabled and at least one
    // question actually renders for this booking. A form containing only system
    // identity questions is skipped when there are no additional guests (or when
    // participants own identity collection).
    if (
      course.userQuestionForm?.isEnabled &&
      hasRenderableQuestions(
        course.userQuestionForm?.questions,
        totalGuestCount,
        collectSystemGuests,
        showParticipantsStep,
      )
    ) {
      steps.push("userform");
    }

    steps.push("basketChoice");

    return steps;
  }, [
    course.userQuestionForm,
    collectSystemGuests,
    totalGuestCount,
    showParticipantsStep,
  ]);

  // Step navigation
  const currentStepIndex = visibleSteps.indexOf(step);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === visibleSteps.length - 1;

  const goToNextStep = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < visibleSteps.length) {
      const nextStep = visibleSteps[nextIndex]!;
      setStep(nextStep);
      onStepChange?.();
    }
  }, [currentStepIndex, visibleSteps, onStepChange]);

  const goToPreviousStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      const prevStep = visibleSteps[prevIndex]!;
      setStep(prevStep);
      onStepChange?.();
    }
  }, [currentStepIndex, visibleSteps, onStepChange]);

  const goToStep = useCallback(
    (targetStep: CheckoutStep) => {
      if (visibleSteps.includes(targetStep)) {
        setStep(targetStep);
        onStepChange?.();
      }
    },
    [visibleSteps, onStepChange],
  );

  // Session selection
  const selectSession = useCallback((session: Session) => {
    setSelectedSession(session);
  }, []);

  // Ticket selection
  const updateTicketSelection = useCallback(
    (ticketTypeId: string, quantity: number) => {
      setTicketSelections((prev) => {
        const existingIndex = prev.findIndex(
          (sel) => sel.ticketTypeId === ticketTypeId,
        );

        let newSelections: TicketSelection[];
        if (quantity === 0) {
          newSelections = prev.filter(
            (sel) => sel.ticketTypeId !== ticketTypeId,
          );
        } else if (existingIndex >= 0) {
          newSelections = [...prev];
          newSelections[existingIndex] = { ticketTypeId, quantity };
        } else {
          newSelections = [...prev, { ticketTypeId, quantity }];
        }

        // Update guests count, accounting for numberOfGuestsPerTicket
        const guestsPerTicket = ticketTypeRef.current?.numberOfGuestsPerTicket ?? 1;
        const totalGuests = newSelections.reduce(
          (sum, sel) => sum + (sel.quantity * guestsPerTicket),
          0,
        );
        base.setPersonalDetails((prev) => {
          const newGuests = [...prev.guests];
          while (newGuests.length < totalGuests) {
            newGuests.push({ ...defaultGuest, guest: true });
          }
          while (newGuests.length > totalGuests && newGuests.length > 1) {
            newGuests.pop();
          }
          return { ...prev, guests: newGuests };
        });

        return newSelections;
      });
    },
    [],
  );

  // Load more dates
  // Pass sessionId on first load so backend can include it in the response
  const loadMoreDates = useCallback(async () => {
    if (base.isLoading || isFullyLoaded) return;

    base.setIsLoading(true);
    try {
      const isFirstLoad = page.current === 0;
      const data = await onLoadMoreDates({
        page: ++page.current,
        limit: 15,
        // Only pass sessionId on first load
        sessionId: isFirstLoad ? initialSessionId : undefined,
      });
      const rawNewSessions = Array.isArray(data) ? data : (data.sessions ?? []);
      // Store raw sessions for session-specific ticket type lookup
      rawSessionsRef.current = [...rawSessionsRef.current, ...rawNewSessions];
      const newSessions = rawNewSessions.map(mapSession);

      setSessions((prev) => [...prev, ...newSessions]);
      setIsFullyLoaded(newSessions.length < 15);
    } catch (error) {
      setIsFullyLoaded(true);
    } finally {
      base.setIsLoading(false);
    }
  }, [base.isLoading, isFullyLoaded, onLoadMoreDates, mapSession, initialSessionId]);

  // Initial load
  useEffect(() => {
    if (initialSessions === undefined) {
      loadMoreDates();
    }
  }, []);

  // Apply discount
  const applyDiscount = useCallback(
    async (code: string) => {
      if (!onValidateDiscount) return;

      const email = base.personalDetails.guests[0]?.email || "";
      base.setIsLoading(true);
      base.clearError();

      try {
        const result = await onValidateDiscount(code, email);
        // API returns discount object directly: { value, type, code, percentage, ... }
        // or null/undefined if invalid
        if (result && result.value !== undefined) {
          base.setDiscount({
            value: result.value,
            type: result.type,
            percentage: result.percentage,
          });
          base.setDiscountCode(result.code || code);
        } else {
          const errorMsg = result?.message || "Invalid discount code";
          base.setError(errorMsg);
          throw new Error(errorMsg);
        }
      } catch (error: any) {
        // Extract error message from axios response or error object
        const errorMsg =
          error?.response?.data?.message ||
          (error instanceof Error ? error.message : null) ||
          "Failed to validate discount";
        base.setError(errorMsg);
        // Re-throw with the proper message so the caller can display it
        throw new Error(errorMsg);
      } finally {
        base.setIsLoading(false);
      }
    },
    [onValidateDiscount, base],
  );

  // Clear discount
  const clearDiscount = useCallback(() => {
    base.setDiscount(null);
    base.setDiscountCode("");
  }, [base]);

  // Validate current step
  const canContinue = useCallback((): boolean => {
    switch (step) {
      case "dates":
        return selectedSession !== null;
      case "tickets":
        return ticketSelections.some((s) => s.quantity > 0);
      case "participants":
        return base.isParticipantsValid;
      case "userform":
        return base.isUserFormValid;
      case "basketChoice":
        return true; // Basket choice step always allows continue
      default:
        return false;
    }
  }, [step, selectedSession, ticketSelections, base]);

  // Get booking data for trial API
  const getBookingData = useCallback(() => {
    const { personalDetails, userFormValues } = base;
    const [guest, ...additionalGuests] = personalDetails.guests;

    // Build tickets array - single ticket type for trial
    const tickets = ticketSelections
      .map((sel) => {
        return initialTicketType
          ? { ...initialTicketType, count: sel.quantity }
          : null;
      })
      .filter(Boolean);

    return {
      bookingType: "regular",
      isTrial, // true for trial bookings, false for drop-in
      isDropIn: !isTrial, // FIX: Add isDropIn flag for drop-in bookings
      tickets,
      user: {
        _id: user?._id || user?.id,
        profile: {
          firstName: guest?.firstName,
          lastName: guest?.lastName,
          phone: guest?.phoneNumber,
        },
        renderEmail: guest?.email,
      },
      checkoutGuestsInformation: personalDetails.guests,
      marketingOptIn: guest?.marketingOptIn,
      courseId: course._id,
      startDateTime: selectedSession?.startDateTime,
      additionalGuests,
      bundleID: bundleID, // uppercase to match backend expectation
      discountCode: base.discountCode,
      credit: 0,
      sessionid: selectedSession?.id,
      deliveryAddress: isSendingMaterial
        ? personalDetails.deliveryAddress
        : null,
      userInputs: userFormValues,
      participants: showParticipantsStep
        ? participantsToPayload(base.participantsValue)
        : undefined,
      // No addOns for trial
      timezone: course.timezone,
      _pricingSummary: {
        grossTotal: total + discountAmount,
        netTotal: total,
        discountAmount,
        discountType: bundleID ? "bundle" : base.discount ? "discount" : undefined,
        currency: course.currency || "GBP",
      },
    };
  }, [
    base,
    ticketSelections,
    initialTicketType,
    selectedSession,
    course,
    user,
    bundleID,
    isSendingMaterial,
    isTrial,
    total,
    discountAmount,
  ]);

  return {
    // State
    step,
    sessions,
    selectedSession,
    ticketTypes,
    ticketSelections,
    userFormValues: base.userFormValues,
    personalDetails: base.personalDetails,
    discount: base.discount,
    discountCode: base.discountCode,
    error: base.error,
    isLoading: base.isLoading,
    isFullyLoaded,
    isConfirmingFreeBooking: base.isConfirmingFreeBooking,
    isUserFormValid: base.isUserFormValid,
    participantsValue: base.participantsValue,
    isParticipantsValid: base.isParticipantsValid,
    buyerAttendeeSeededRef: base.buyerAttendeeSeededRef,
    showParticipantsStep,

    // Computed
    total,
    discountAmount,
    isFree,
    visibleSteps,
    totalGuestCount,
    ticketSeatGroups,
    collectSystemGuests,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    course,
    bundleID,
    availability: selectedSession?.availability ?? 0,
    remainingAvailability,

    // Actions
    goToNextStep,
    goToPreviousStep,
    goToStep,
    selectSession,
    updateTicketSelection,
    updateUserFormValue: base.updateUserFormValue,
    setUserFormValues: base.setUserFormValues,
    setIsUserFormValid: base.setIsUserFormValid,
    setParticipantsValue: base.setParticipantsValue,
    setIsParticipantsValid: base.setIsParticipantsValid,
    updatePersonalDetails: base.updatePersonalDetails,
    updateDeliveryAddress: base.updateDeliveryAddress,
    loadMoreDates,
    applyDiscount,
    clearDiscount,
    canContinue,
    getBookingData,
    setIsConfirmingFreeBooking: base.setIsConfirmingFreeBooking,
    clearError: base.clearError,
  };
}
